import {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {createPendingSaveBarrier, hasPendingSaves, waitForPendingSaves} from "../utilities/saveInFlightManager";

export default function useDeferredTemplateSave(save, onDeferred) {
    const saveInFlightCount = useSelector(state => state.saveInFlight?.count ?? 0);
    const saveRef = useRef(save);
    const queuedSaveRef = useRef(null);
    const saveRequestedRef = useRef(false);
    const mountedRef = useRef(false);
    const [queueVersion, setQueueVersion] = useState(0);

    saveRef.current = save;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            queuedSaveRef.current?.barrier.close();
            queuedSaveRef.current = null;
            saveRequestedRef.current = false;
        };
    }, []);

    const executeSave = useCallback((...args) => {
        if (saveRequestedRef.current) {
            return undefined;
        }

        saveRequestedRef.current = true;
        let result;
        try {
            result = saveRef.current(...args);
        }
        catch (error) {
            saveRequestedRef.current = false;
            throw error;
        }

        const completion = result && typeof result.then === "function"
            ? result
            : (hasPendingSaves() ? waitForPendingSaves() : Promise.resolve());
        Promise.resolve(completion).then(
            () => { saveRequestedRef.current = false; },
            () => { saveRequestedRef.current = false; }
        );
        return result;
    }, []);

    const observeQueuedSaves = useCallback(queue => {
        queue.result = null;
        queue.barrier.wait().then(
            () => ({succeeded: true}),
            error => ({succeeded: false, error})
        ).then(result => {
            if (mountedRef.current && queuedSaveRef.current === queue) {
                queue.result = result;
                setQueueVersion(version => version + 1);
            }
        });
    }, []);

    useEffect(() => {
        const queuedSave = queuedSaveRef.current;
        if (!queuedSave?.result) {
            return;
        }

        if (!queuedSave.result.succeeded) {
            queuedSave.barrier.close();
            queuedSaveRef.current = null;
            return;
        }

        if (saveInFlightCount > 0) {
            return;
        }

        if (queuedSave.barrier.hasUnobservedSaves() || hasPendingSaves()) {
            observeQueuedSaves(queuedSave);
            return;
        }

        queuedSave.barrier.close();
        queuedSaveRef.current = null;
        executeSave(...queuedSave.args);
    }, [executeSave, observeQueuedSaves, queueVersion, saveInFlightCount]);

    return useCallback((...args) => {
        if (saveRequestedRef.current || queuedSaveRef.current) {
            return undefined;
        }

        if (!hasPendingSaves()) {
            return executeSave(...args);
        }

        if (!queuedSaveRef.current) {
            const queue = {
                args,
                barrier: createPendingSaveBarrier(),
                result: null
            };
            queuedSaveRef.current = queue;
            observeQueuedSaves(queue);
            onDeferred?.();
        }

        return undefined;
    }, [executeSave, observeQueuedSaves, onDeferred]);
}
